import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useState } from "react";
import { Alert, Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useRequestPasswordResetMutation } from "@/redux/apis/auth/authApi";
import { useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";

interface ProfilePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfilePasswordModal = ({ isOpen, onClose }: ProfilePasswordModalProps) => {
  const { logout } = useKindeAuth();
  const accountId = useAppSelector(accountIdSelector);
  const [requestPasswordReset, { isLoading }] = useRequestPasswordResetMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChangePasswordClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    try {
      setErrors([]);
      await requestPasswordReset(accountId).unwrap();

      // Close modal and logout with redirect to login
      setShowConfirmation(false);
      onClose();
      logout();
    } catch (e) {
      setErrors(getErrorMessages(e as Error));
      setShowConfirmation(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setErrors([]);
    onClose();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setErrors([]);
    onClose(); // Close everything instead of going back to first modal
  };

  return (
    <>
      {/* Main Modal */}
      <Modal open={isOpen && !showConfirmation} onClose={handleClose} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-primary-light p-3">
                <Icon name="Lock1" variant="Bold" size={36} className="text-primary" />
              </div>
            </div>
            <Typography variant="h1">Change Password</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              To change your password, you&apos;ll be signed out and redirected to create a new password.
            </Typography>
          </div>

          {errors.length > 0 && (
            <div className="my-4">
              {errors.map((e, i) => (
                <Alert key={i} type="danger" message={e} className="my-4" />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleClose} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleChangePasswordClick} className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal open={showConfirmation} onClose={handleCancelConfirmation} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-warning-light p-3">
                <Icon name="Warning2" variant="Bold" size={36} className="text-warning" />
              </div>
            </div>
            <Typography variant="h1">Confirm Password Change</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              You&apos;ll be signed out and redirected to the sign-in page to create a new password. Are you sure you want to continue?
            </Typography>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleCancelConfirmation} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading} className="w-full md:flex-1" size="lg">
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Spinner variant="light" />
                  <span className="ml-2">Processing...</span>
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
